/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 担当割当の編集モーダル（1 サービス分）。オペ担 / review_sql 当番のローテーション queue + cadence と、日付指定のカバー（当日=シフト代理 / 将来日=休日オペたん）を 1 セクションで編集し、保存で PUT body（OpsAssignmentEdit）を親へ返す。カバーは (日付 + 役割) を単一キーに upsert し、スケジュールプレビューにも overlay する。担当者候補は /members のエンジニア nickName を使う。開くたびに対象サービスの現状値で下書きを初期化する
 * @graph-connects none
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { MultiPicker } from '@cortex/web-design-system/components/ui/MultiPicker';
import { Select, type SelectOption } from '@cortex/web-design-system/components/ui/Select';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Label } from '@cortex/web-design-system/components/ui/Label';
import { coverFor, formatMd, jstDateKey, rotationInfo, rotationSchedule } from '@cortex/ops-core';
import { TabButton } from '../../ui/Tabs/TabButton.js';
import { TabGroup } from '../../ui/Tabs/TabGroup.js';
import { ReorderableChips } from './ReorderableChips.js';
import type {
  OpsAssignment,
  OpsCover,
  OpsRotationCadence,
  OpsService,
} from '../../../types/ops.js';
import type { OpsAssignmentEdit } from '../../../lib/api/ops.js';

/** カバーの役割。 @graph-connects none */
type CoverRole = 'opetan' | 'reviewer';

/** 頻度タブ定義（label は t() で解決する）。 @graph-connects none */
const CADENCE_TABS: { key: OpsRotationCadence; labelKey: string }[] = [
  { key: 'daily', labelKey: 'ops.assignments.cadence.daily' },
  { key: 'weekly', labelKey: 'ops.assignments.cadence.weekly' },
  { key: 'monthly', labelKey: 'ops.assignments.cadence.monthly' },
];

/** カバーを (日付 + 役割) 単一キーで upsert し、日付昇順→役割順で返す（同じ日付+役割は置き換え）。 @graph-connects none */
export function upsertCover(covers: OpsCover[], entry: OpsCover): OpsCover[] {
  const others = covers.filter((c) => !(c.date === entry.date && c.role === entry.role));
  return [...others, entry].sort(
    (a, b) => a.date.localeCompare(b.date) || a.role.localeCompare(b.role),
  );
}

/** 指定 (日付 + 役割) のカバーを外す。 @graph-connects none */
export function removeCoverByKey(
  covers: OpsCover[],
  date: string,
  role: OpsCover['role'],
): OpsCover[] {
  return covers.filter((c) => !(c.date === date && c.role === role));
}

/**
 * 追加行が妥当か（日付・担当が揃い、過去日でない）。YYYY-MM-DD は辞書順=日付順なので `date >= todayKey` の
 * 文字列比較で過去日を弾く（当日は追加可＝境界は当日を含む）。
 * @graph-connects none
 */
export function isCoverAddable(date: string, who: string, todayKey: string): boolean {
  return date !== '' && who !== '' && date >= todayKey;
}

/** ScheduleStrip の props。 */
interface ScheduleStripProps {
  /** ローテーション queue。 */
  queue: string[];
  /** 頻度。 */
  cadence: OpsRotationCadence;
  /** この strip が表す役割（カバー overlay の照合に使う）。 */
  role: CoverRole;
  /** 下書きのカバー（各期間の開始日に overlay する）。 */
  covers: OpsCover[];
  /** 基準日時。 */
  now: Date;
  /** 過去に遡る期間数（default 0＝過去は出さない。実績は History タブが担当）。 */
  back?: number;
  /** 先読みする期間数（default 4）。 */
  fwd?: number;
}

/**
 * 今〜先（upcoming）の担当スケジュールを 1 行チップで表示する（今はアクセント）。各期間の開始日に該当役割の
 * 日付指定カバーがあれば、その担当と「カバー」マーカーへ差し替える（当日・将来日どちらのカバーも可視化する）。
 * @graph-connects none
 */
function ScheduleStrip({
  queue,
  cadence,
  role,
  covers,
  now,
  back = 0,
  fwd = 4,
}: ScheduleStripProps): ReactNode {
  const { t } = useTranslation();
  if (queue.length < 2) return null;
  const sched = rotationSchedule(queue, cadence, now, back, fwd);
  return (
    <div className="flex flex-wrap gap-1.5 text-xs">
      {sched.map((s) => {
        const cover = coverFor(covers, role, s.start);
        return (
          <span
            key={s.offset}
            title={
              s.offset === 0
                ? t('ops.assignments.scheduleTitle.now')
                : t('ops.assignments.scheduleTitle.upcoming')
            }
            className={
              s.offset === 0
                ? 'rounded bg-[var(--color-accent-primary)] px-1.5 py-0.5 font-semibold text-white'
                : 'rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[var(--color-text-secondary)]'
            }
          >
            {formatMd(s.start)} {cover ?? s.who}
            {cover !== null && (
              <span className="ml-1 rounded bg-[var(--color-warning)] px-1 text-[10px] font-semibold text-white">
                {t('ops.assignments.modal.coverMarker')}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

/** OpsAssignmentEditModal の props。 */
export interface OpsAssignmentEditModalProps {
  /** 編集対象の割当（null = 閉じている）。開くたびにこの現状値で下書きを初期化する。 */
  assignment: OpsAssignment | null;
  /** 担当者候補（エンジニア nickName）。queue / cover で共通の単一 source。 */
  engineerOptions: SelectOption[];
  /** 基準日時（今日の導出・プレビュー・日付 min に使う）。 */
  now: Date;
  /** 保存 PUT 実行中か（ボタンの loading 用）。 */
  isSaving: boolean;
  /** 直前の保存が失敗したか（モーダルを開いたままエラー表示・再保存させる）。 */
  saveFailed: boolean;
  /** 閉じる（キャンセル）。 */
  onClose: () => void;
  /** 保存を確定する（サービス + 編集内容）。 */
  onSave: (service: OpsService, edit: OpsAssignmentEdit) => void;
}

/**
 * 1 サービス分の担当割当を編集するモーダル。オペ担 / reviewer の queue + cadence と、日付指定のカバー
 * （当日=シフト代理 / 将来日=休日オペたん）を 1 セクションで編集し、保存で OpsAssignmentEdit を親へ返す。
 * 開くたびに対象サービスの現状値で下書きを初期化する。過去日のカバーは追加できない（date input の min + 明示ガード）。
 * @graph-connects none
 */
export function OpsAssignmentEditModal({
  assignment,
  engineerOptions,
  now,
  isSaving,
  saveFailed,
  onClose,
  onSave,
}: OpsAssignmentEditModalProps): ReactNode {
  const { t } = useTranslation();
  const [draftOpetanQueue, setDraftOpetanQueue] = useState<string[]>([]);
  const [draftOpetanCadence, setDraftOpetanCadence] = useState<OpsRotationCadence>('weekly');
  const [draftReviewerQueue, setDraftReviewerQueue] = useState<string[]>([]);
  const [draftReviewerCadence, setDraftReviewerCadence] = useState<OpsRotationCadence>('weekly');
  const [draftCovers, setDraftCovers] = useState<OpsCover[]>([]);
  const [draftNewDate, setDraftNewDate] = useState('');
  const [draftNewRole, setDraftNewRole] = useState<CoverRole>('opetan');
  const [draftNewWho, setDraftNewWho] = useState('');

  // 対象サービスが変わる（別サービスを開く）たびに、その現状値で下書きを初期化する。
  useEffect(() => {
    if (assignment === null) return;
    setDraftOpetanQueue(assignment.opetanQueue);
    setDraftOpetanCadence(assignment.opetanCadence);
    setDraftReviewerQueue(assignment.reviewerQueue);
    setDraftReviewerCadence(assignment.reviewerCadence);
    setDraftCovers(
      [...assignment.covers].sort(
        (a, b) => a.date.localeCompare(b.date) || a.role.localeCompare(b.role),
      ),
    );
    setDraftNewDate('');
    setDraftNewRole('opetan');
    setDraftNewWho('');
  }, [assignment]);

  const roleLabel = (role: CoverRole): string =>
    role === 'opetan'
      ? t('ops.assignments.modal.coverOpetan')
      : t('ops.assignments.modal.coverReviewer');

  const roleOptions: SelectOption[] = useMemo(
    () => [
      { value: 'opetan', label: t('ops.assignments.modal.coverOpetan') },
      { value: 'reviewer', label: t('ops.assignments.modal.coverReviewer') },
    ],
    [t],
  );
  const whoOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('ops.assignments.modal.coverWhoPlaceholder') },
      ...engineerOptions,
    ],
    [engineerOptions, t],
  );
  const todayKey = jstDateKey(now);

  const draftOpetanInfo = rotationInfo(
    draftOpetanQueue,
    draftOpetanCadence,
    coverFor(draftCovers, 'opetan', now),
    now,
  );
  const draftReviewerInfo = rotationInfo(
    draftReviewerQueue,
    draftReviewerCadence,
    coverFor(draftCovers, 'reviewer', now),
    now,
  );

  const canAddCover = isCoverAddable(draftNewDate, draftNewWho, todayKey);

  const addCover = (): void => {
    if (!canAddCover) return;
    setDraftCovers((prev) =>
      upsertCover(prev, { date: draftNewDate, role: draftNewRole, who: draftNewWho }),
    );
    setDraftNewDate('');
    setDraftNewWho('');
  };

  const removeCover = (date: string, role: CoverRole): void => {
    setDraftCovers((prev) => removeCoverByKey(prev, date, role));
  };

  const save = (): void => {
    if (assignment === null) return;
    onSave(assignment.service, {
      opetanQueue: draftOpetanQueue,
      opetanCadence: draftOpetanCadence,
      reviewerQueue: draftReviewerQueue,
      reviewerCadence: draftReviewerCadence,
      covers: draftCovers,
    });
  };

  return (
    <Modal
      open={assignment !== null}
      onClose={onClose}
      size="lg"
      title={
        assignment === null
          ? ''
          : t('ops.assignments.modal.title', { service: assignment.serviceLabel })
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          {saveFailed && (
            <span className="mr-auto text-xs text-[var(--color-error)]">
              {t('ops.assignments.modal.saveFailed')}
            </span>
          )}
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('ops.assignments.modal.cancel')}
          </Button>
          <Button type="button" variant="primary" loading={isSaving} onClick={save}>
            {t('ops.assignments.modal.save')}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="space-y-2">
          <Label>{t('ops.assignments.modal.opetanQueueLabel')}</Label>
          <MultiPicker
            aria-label={t('ops.assignments.modal.opetanQueueAria')}
            options={engineerOptions}
            value={draftOpetanQueue}
            onChange={(value) => {
              setDraftOpetanQueue(value);
            }}
          />
          <ReorderableChips items={draftOpetanQueue} onChange={setDraftOpetanQueue} />
          <TabGroup ariaLabel={t('ops.assignments.modal.opetanCadenceAria')}>
            {CADENCE_TABS.map((c) => (
              <TabButton
                key={c.key}
                active={draftOpetanCadence === c.key}
                label={t(c.labelKey)}
                disabled={draftOpetanQueue.length < 2}
                onClick={() => {
                  setDraftOpetanCadence(c.key);
                }}
              />
            ))}
          </TabGroup>
          <p className="text-xs text-[var(--color-text-muted)]">
            {draftOpetanQueue.length > 1
              ? t('ops.assignments.modal.opetanOnNow', {
                  scheduled: draftOpetanInfo.scheduled ?? '—',
                  next: draftOpetanInfo.next ?? '—',
                  from:
                    draftOpetanInfo.nextStart !== null
                      ? t('ops.assignments.modal.onNowFrom', {
                          date: formatMd(draftOpetanInfo.nextStart),
                        })
                      : '',
                  cadence: draftOpetanCadence,
                })
              : t('ops.assignments.modal.opetanAddMore')}
          </p>
          {draftOpetanQueue.length > 1 && (
            <div className="space-y-1">
              <span className="text-xs text-[var(--color-text-muted)]">
                {t('ops.assignments.modal.upcoming')}
              </span>
              <ScheduleStrip
                queue={draftOpetanQueue}
                cadence={draftOpetanCadence}
                role="opetan"
                covers={draftCovers}
                now={now}
              />
            </div>
          )}
        </div>

        <div className="space-y-2 border-t border-[var(--color-border)] pt-4">
          <Label>{t('ops.assignments.modal.reviewerQueueLabel')}</Label>
          <MultiPicker
            aria-label={t('ops.assignments.modal.reviewerQueueAria')}
            options={engineerOptions}
            value={draftReviewerQueue}
            onChange={(value) => {
              setDraftReviewerQueue(value);
            }}
          />
          <ReorderableChips items={draftReviewerQueue} onChange={setDraftReviewerQueue} />
          <TabGroup ariaLabel={t('ops.assignments.modal.reviewerCadenceAria')}>
            {CADENCE_TABS.map((c) => (
              <TabButton
                key={c.key}
                active={draftReviewerCadence === c.key}
                label={t(c.labelKey)}
                disabled={draftReviewerQueue.length < 2}
                onClick={() => {
                  setDraftReviewerCadence(c.key);
                }}
              />
            ))}
          </TabGroup>
          <p className="text-xs text-[var(--color-text-muted)]">
            {draftReviewerQueue.length > 1
              ? t('ops.assignments.modal.reviewerOnNow', {
                  scheduled: draftReviewerInfo.scheduled ?? '—',
                  next: draftReviewerInfo.next ?? '—',
                  from:
                    draftReviewerInfo.nextStart !== null
                      ? t('ops.assignments.modal.onNowFrom', {
                          date: formatMd(draftReviewerInfo.nextStart),
                        })
                      : '',
                  cadence: draftReviewerCadence,
                })
              : t('ops.assignments.modal.reviewerAddMore')}
          </p>
          {draftReviewerQueue.length > 1 && (
            <div className="space-y-1">
              <span className="text-xs text-[var(--color-text-muted)]">
                {t('ops.assignments.modal.upcoming')}
              </span>
              <ScheduleStrip
                queue={draftReviewerQueue}
                cadence={draftReviewerCadence}
                role="reviewer"
                covers={draftCovers}
                now={now}
              />
            </div>
          )}
        </div>

        <div className="space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
          <Label htmlFor="ops-cover-date">{t('ops.assignments.modal.coverSectionLabel')}</Label>
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('ops.assignments.modal.coverSectionHelper')}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
            <Input
              id="ops-cover-date"
              type="date"
              min={todayKey}
              value={draftNewDate}
              aria-label={t('ops.assignments.modal.coverDateAria')}
              onChange={(e) => {
                setDraftNewDate(e.target.value);
              }}
            />
            <Select
              options={roleOptions}
              value={draftNewRole}
              aria-label={t('ops.assignments.modal.coverRoleAria')}
              onChange={(e) => {
                setDraftNewRole(e.target.value as CoverRole);
              }}
            />
            <Select
              options={whoOptions}
              value={draftNewWho}
              aria-label={t('ops.assignments.modal.coverWhoAria')}
              onChange={(e) => {
                setDraftNewWho(e.target.value);
              }}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!canAddCover}
              onClick={addCover}
            >
              {t('ops.assignments.modal.coverAdd')}
            </Button>
          </div>
          {draftCovers.length === 0 ? (
            <p className="text-xs text-[var(--color-text-muted)]">
              {t('ops.assignments.modal.coverEmpty')}
            </p>
          ) : (
            <ul className="space-y-1">
              {draftCovers.map((c) => (
                <li
                  key={`${c.date}-${c.role}`}
                  className="flex flex-wrap items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1 text-sm"
                >
                  <span className="font-mono text-xs text-[var(--color-text-secondary)]">
                    {c.date}
                  </span>
                  {c.date === todayKey && (
                    <Badge variant="warning" size="sm">
                      {t('ops.assignments.modal.coverTodayBadge')}
                    </Badge>
                  )}
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {roleLabel(c.role)}
                  </span>
                  <Badge variant="info" size="sm">
                    {c.who}
                  </Badge>
                  <button
                    type="button"
                    className="ml-auto text-[var(--color-text-muted)] hover:text-[var(--color-error)]"
                    aria-label={t('ops.assignments.modal.coverRemove', {
                      date: c.date,
                      role: roleLabel(c.role),
                    })}
                    onClick={() => {
                      removeCover(c.date, c.role);
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
